import { request } from './client';

export interface CalendarAttendee {
  name: string;
  email: string;
}

export type SourceId = 'outlook' | 'zoom' | 'freshdesk';

// The caller's own reply to the invitation, and how the source shows the time —
// both in the source's vocabulary (MSGraph responseStatus.response / showAs).
export type InviteResponse =
  | 'none' | 'organizer' | 'tentativelyAccepted' | 'accepted' | 'declined' | 'notResponded';
export type Availability =
  | 'free' | 'tentative' | 'busy' | 'oof' | 'workingElsewhere' | 'unknown';

export interface SourceEvent {
  id: string;
  subject: string;
  start: string;
  end: string;
  isAllDay: boolean;
  attendees: CalendarAttendee[];
  location: string | null;
  organizerEmail: string | null;
  organizerName: string | null;
  source: SourceId;
  externalRef: string;
  sourceClient: string | null;
  tags: string[];
  durationKind: 'logged' | 'estimated';
  isRecurring: boolean;
  // What the source records about the item beyond its calendar-shaped fields.
  // Null where the source has no such concept — an Outlook event has no ticket
  // number, a Freshdesk ticket no invitation.
  sourceItemRef: string | null;
  sourceItemType: string | null;
  sourceNote: string | null;
  sourceBillable: boolean | null;
  sourceItemUrl: string | null;
  inviteResponse: InviteResponse | null;
  availability: Availability | null;
  isOrganizer: boolean | null;
}

export interface CalendarEventsResponse {
  events: SourceEvent[];
}

export interface MSGraphSignInStartResponse {
  userCode: string;
  verificationUri: string;
  expiresAt: string;
  interval: number;
}

export type PollStatus = 'pending' | 'authorized' | 'expired';

export interface MSGraphSignInPollResponse {
  status: PollStatus;
  expiresAt?: string;
}

// `tz` must match the zone the draft was generated in: Freshdesk event ids
// embed the zone-local day, so reading in another zone yields ids that don't
// join back to the draft's contributing events.
export function getCalendarEvents(from?: string, to?: string, tz?: string): Promise<CalendarEventsResponse> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (tz) params.set('tz', tz);
  const qs = params.toString();
  return request<CalendarEventsResponse>(`/api/calendar/events${qs ? `?${qs}` : ''}`);
}

export function graphSignInStart(): Promise<MSGraphSignInStartResponse> {
  return request<MSGraphSignInStartResponse>('/api/msgraph/start', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export function graphSignInPoll(): Promise<MSGraphSignInPollResponse> {
  return request<MSGraphSignInPollResponse>('/api/msgraph/poll', {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}
